import { useMemo, useState, type VoidFunctionComponent } from 'react';
import type { MultiValue } from 'react-select';
import { Modal } from 'react-bootstrap';
import Button, { ButtonGroup, LoaderButton } from '../../../../../../generic/Button';
import useUsersFromOrg from '../../../../../../../hooks/api/users/useUsersFromOrg';
import { window } from '../../../../../../../globals';
import { selectOptionFactory } from '../../../../../../../utils';
import { FormGroup, FormLabelVariant, LabeledSelect } from '../../../../../../generic';
import useSelectRoles from '../../../hooks/useSelectRoles';
import type { SelectOption } from '../../../../../../../types';
import { useAddMembersToTeamGroup } from '../../../../../../../hooks/api';
import { useAddToast } from '../../../../../../../contexts/ToastContext';
import { getErrorMessage } from '../../../../../../../ReactQuery';
import { plural } from '../../../../../../../../shared/helpers';

type Props = {
  isOpen: boolean;
  onHide: () => void;
  teamGroupId: number;
  teamGroupName: string;
  previouslyAddedMembers: Set<number>;
};

/**
 * Modal for adding members to a team group.
 *
 * @param {Props} props - The component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {string} props.teamGroupName - The name of the team group
 * @param {() => void} props.onHide - Callback when the modal is closed
 */
const AddTeamGroupMemberModal: VoidFunctionComponent<Props> = ({
  isOpen,
  onHide,
  teamGroupId,
  teamGroupName,
  previouslyAddedMembers,
}) => {
  const addToast = useAddToast();
  const { users, isLoading: isLoadingUsers } = useUsersFromOrg(window.user?.organizationId as number);
  const userOptions = useMemo(() => {
    return users
      //  Remove previously added members from the list of users.
      .filter(user => !previouslyAddedMembers.has(user.id))
      .map(user => selectOptionFactory(user.id, user.username || user.email));
  }, [users, previouslyAddedMembers]);
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());

  const onUserSelect = (nextSelectedUsers: MultiValue<SelectOption<number>>) =>
    setSelectedUsers(new Set(nextSelectedUsers.map(user => user.value)));

  const {
    roleOptions,
    onChangeRole,
    selectedRole,
    isLoading: isLoadingRoles,
  } = useSelectRoles();

  const { mutateAsync: addMembersToTeamGroup, isLoading: isSubmitting } = useAddMembersToTeamGroup(teamGroupId, {
    action: 'add',
    users: Array.from(selectedUsers).map(id => ({ id, roleId: selectedRole?.value })),
  });

  const onAdd = async () => {
    try {
      await addMembersToTeamGroup();
      addToast({
        type: 'success',
        content: `Added ${selectedUsers.size} ${plural(selectedUsers.size, 'user')} to "${teamGroupName}"`,
      });
      onHide();
    } catch (error) {
      addToast({
        type: 'error',
        content: getErrorMessage(error, `Failed to add ${plural(selectedUsers.size, 'user')} to "${teamGroupName}"`),
      });
    }
  };

  const isAddButtonDisabled = !selectedUsers.size || !selectedRole || isSubmitting;

  return (
    <Modal
      show={isOpen}
      onHide={onHide}
    >
      <Modal.Header closeButton>
        <Modal.Title className="margin-bottom-sm">
          Add Members to &ldquo;{teamGroupName}&rdquo;
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormGroup>
          <LabeledSelect
            id="add-team-group-member-modal-user-select"
            label="Users"
            placeholder="Select users"
            options={userOptions}
            onChange={onUserSelect}
            variant={FormLabelVariant.Filter}
            isDisabled={isLoadingUsers}
            isMulti
          />
          <LabeledSelect
            id="add-team-group-member-modal-default-role-select"
            label="Role (for added users)"
            placeholder="Select a role"
            options={roleOptions}
            onChange={onChangeRole}
            variant={FormLabelVariant.Filter}
            isDisabled={isLoadingRoles}
          />
        </FormGroup>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type="secondary"
            size="medium"
            onClick={onHide}
          >
            Cancel
          </Button>
          <LoaderButton
            type="primary"
            size="medium"
            onClick={onAdd}
            isDisabled={isAddButtonDisabled}
            isLoading={isSubmitting}
          >
            Add
          </LoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default AddTeamGroupMemberModal;
